/**
 * Single source for public marketing navigation: footer columns and the
 * desktop Features mega menu. Use cases stay DB-backed in `useUseCaseMenu`.
 *
 * Footer lists the full site map for crawlers. The Features menu is a curated
 * subset aimed at marketing and discoverable product surfaces — not auth-gated
 * app chrome like Files or Deck workspace.
 */

/** Hub route for the Features nav trigger and “explore all” footer row. */
export const FEATURES_NAV_HUB = '/features';

/** Hub route for the Docs nav trigger. */
export const DOCS_NAV_HUB = '/docs';

/**
 * Priority persona links for homepage body copy and mid-page CTAs.
 * Anchor text for free-thinkers is protected — keep in sync with
 * `backend/utils/useCaseLinks.js`.
 */
export const PRIORITY_USE_CASE_LINKS = [
  { href: '/use-cases/free-thinkers', label: 'Uncensored AI chat for free thinkers' },
  { href: '/use-cases/small-business', label: 'AI for small businesses' },
  { href: '/use-cases/veterans', label: 'AI for veterans' },
  { href: '/use-cases/churches', label: 'AI for churches & ministries' },
  { href: '/use-cases/homeschoolers', label: 'AI for homeschoolers' },
  { href: '/use-cases/writers', label: 'AI for writers' },
  { href: '/use-cases/developers', label: 'AI for developers' },
  { href: '/use-cases/marketers', label: 'AI for marketers' },
  { href: '/use-cases/independent-media', label: 'Independent media AI tools' },
];

/** Routes that belong in the public footer but not the Features mega menu. */
const FEATURES_EXCLUDED_HREFS = new Set([
  '/pricing', // top-level nav item — avoid double-active highlight
  '/business',
  '/contact',
  '/feedback',
  '/about',
  '/brand',
  '/docs',
  '/learn',
  '/api',
  '/careers',
]);

/**
 * Full footer columns — comprehensive crawl graph. Library omits auth-heavy app
 * routes that logged-out visitors cannot use meaningfully.
 */
export const FOOTER_NAV_COLUMNS = [
  {
    title: 'Workspace',
    links: [
      { route: '/models', label: 'Models' },
      { route: '/tools', label: 'AI tools' },
      { route: '/voice', label: 'Voice mode' },
      { route: '/agents', label: 'Bots' },
      { route: '/topics', label: 'Topics' },
      { route: '/tasks', label: 'Tasks' },
      { route: '/tasks/browse', label: 'Task templates' },
      { route: '/decks/recipes', label: 'Deck recipes' },
      { route: '/prompts', label: 'Prompt Library' },
      { route: '/features', label: 'All features' },
    ],
  },
  {
    title: 'Library',
    links: [
      { route: '/images', label: 'Images' },
      { route: '/videos', label: 'Videos' },
      { route: '/music', label: 'Music' },
      { route: '/explore', label: 'Explore' },
      { route: '/bookmarks', label: 'Bookmarks' },
    ],
  },
  {
    title: 'Discover',
    links: [
      { route: '/explore', label: 'Explore' },
      { route: '/topics', label: 'Topics' },
      { route: '/agents', label: 'AI Bots' },
      { route: '/learn', label: 'AI University' },
      { route: '/blog', label: 'Blog' },
      { route: '/changelog', label: "What's new" },
      { route: '/llms', label: 'LLM directory' },
      { route: '/use-cases', label: 'Use cases' },
      { route: '/right-wing-ai', label: 'Conservative AI' },
      { route: '/based-benchmark', label: 'Based Benchmark' },
      { route: '/alternatives', label: 'AI chat alternatives' },
      { route: '/chatgpt-alternative', label: 'ChatGPT alternative' },
      { route: '/poe-alternative', label: 'Poe alternative' },
      { route: '/monica-alternative', label: 'Monica alternative' },
      { route: '/chathub-alternative', label: 'ChatHub alternative' },
      { route: '/typingmind-alternative', label: 'TypingMind alternative' },
      { route: '/t3chat-alternative', label: 't3.chat alternative' },
      { route: '/galaxy-ai-alternative', label: 'Galaxy AI alternative' },
      { route: '/openrouter-alternative', label: 'OpenRouter alternative' },
      { route: '/free-ai-chat', label: 'Free AI chat' },
      { route: '/pricing', label: 'Pricing' },
      { route: '/business', label: 'Gab AI Business', webOnly: true },
      { route: '/download', label: 'Download' },
      { route: '/extension', label: 'Browser extension' },
    ],
  },
  {
    title: 'Developers',
    links: [
      { route: '/api', label: 'API Router' },
      { route: '/docs/api', label: 'API overview' },
      { route: '/docs/api-auth', label: 'API auth' },
      { route: '/docs/api-endpoints', label: 'API endpoints' },
      { route: '/docs/codex-cli', label: 'Codex CLI' },
      { route: '/docs/cursor-ide', label: 'Cursor IDE' },
      { route: '/docs/claude-code', label: 'Claude Code' },
      { route: '/docs/opencode', label: 'OpenCode' },
      { route: '/docs/prime-agent', label: 'Prime Agent' },
    ],
  },
  {
    title: 'Documentation',
    links: [
      { route: '/docs', label: 'Docs home' },
      { route: '/docs/quickstart', label: 'Quickstart' },
      { route: '/docs/business', label: 'Gab AI Business' },
      { route: '/docs/overview', label: 'Overview' },
      { route: '/docs/models', label: 'Models' },
      { route: '/docs/tools', label: 'Tools' },
      { route: '/docs/editors', label: 'Docs, Slides & Sheets' },
      { route: '/docs/tasks', label: 'Tasks' },
      { route: '/docs/chat', label: 'Chat' },
    ],
  },
  {
    title: 'Company',
    links: [
      { route: '/about', label: 'About' },
      { route: '/brand', label: 'Brand guidelines' },
      { route: '/contact', label: 'Contact' },
      { route: '/feedback', label: 'Feedback' },
      { route: '/terms-of-service', label: 'Terms of Service' },
      { route: '/terms-of-sale', label: 'Terms of Sale' },
      { route: '/privacy-policy', label: 'Privacy Policy' },
      { route: '/dmca', label: 'DMCA' },
    ],
  },
];

/** Curated mega-menu columns — marketing routes only. */
export const FEATURES_NAV_COLUMNS = [
  {
    label: 'Create',
    items: [
      { href: '/images', label: 'AI image generator' },
      { href: '/videos', label: 'AI video generator' },
      { href: '/music', label: 'AI music generator' },
      { href: '/voice', label: 'AI voice chat' },
      { href: '/models', label: 'AI models hub' },
    ],
  },
  {
    label: 'Workspace',
    items: [
      { href: '/tools', label: 'AI tools' },
      { href: '/agents', label: 'Bots' },
      { href: '/tasks', label: 'Tasks' },
      { href: '/tasks/browse', label: 'Task templates' },
      { href: '/decks/recipes', label: 'Deck recipes' },
      { href: '/prompts', label: 'Prompt library' },
      { href: '/extension', label: 'Browser extension' },
    ],
  },
  {
    label: 'Discover',
    items: [
      { href: '/explore', label: 'Explore' },
      { href: '/topics', label: 'Topics' },
      { href: '/blog', label: 'Blog' },
      { href: '/changelog', label: "What's new" },
      { href: '/llms', label: 'LLM directory' },
    ],
  },
  {
    label: 'Why Gab AI',
    items: [
      { href: '/right-wing-ai', label: 'Conservative & Christian AI' },
      { href: '/based-benchmark', label: 'Based Benchmark' },
      { href: '/chatgpt-alternative', label: 'ChatGPT alternative' },
      // One row for the whole comparison cluster. The other competitor
      // pages sit under this hub rather than in the menu: they are entered from
      // search, not browsed to, and a stack of near-identical rows would bury
      // the items above them.
      { href: '/alternatives', label: 'Compare alternatives' },
      { href: '/free-ai-chat', label: 'Free AI chat' },
      { href: '/switch', label: 'Import your chats' },
    ],
  },
];

/** Docs, learn, and API mega menu — replaces the flat “Docs” nav item. */
export const DOCS_NAV_COLUMNS = [
  {
    label: 'Documentation',
    items: [
      { href: '/docs', label: 'Docs home' },
      { href: '/brand', label: 'Brand guidelines' },
      { href: '/docs/quickstart', label: 'Quickstart' },
      { href: '/docs/business', label: 'Gab AI Business' },
      { href: '/docs/overview', label: 'Overview' },
      { href: '/docs/models', label: 'Models guide' },
      { href: '/docs/tools', label: 'Tools guide' },
      { href: '/docs/editors', label: 'Docs, Slides & Sheets' },
      { href: '/docs/chat', label: 'Chat' },
    ],
  },
  {
    label: 'AI University',
    items: [
      { href: '/learn', label: 'Learn hub' },
      { href: '/learn/what-is-ai', label: 'What is AI?' },
      { href: '/learn/first-prompt', label: 'Your first prompt' },
      { href: '/learn/text-to-image', label: 'Text to image' },
      { href: '/learn/music-generation', label: 'Music generation' },
      { href: '/learn/custom-agents', label: 'Custom bots' },
      { href: '/learn/agentic-workflows', label: 'Agentic workflows' },
      { href: '/learn/memory', label: 'AI memory' },
    ],
  },
  {
    label: 'API',
    items: [
      { href: '/api', label: 'API Router' },
      { href: '/docs/api', label: 'API overview' },
      { href: '/docs/api-auth', label: 'API authentication' },
      { href: '/docs/api-endpoints', label: 'API endpoints' },
    ],
  },
  {
    label: 'Integrations',
    items: [
      { href: '/docs/cursor-ide', label: 'Cursor IDE' },
      { href: '/docs/claude-code', label: 'Claude Code' },
      { href: '/docs/codex-cli', label: 'Codex CLI' },
      { href: '/docs/opencode', label: 'OpenCode' },
      { href: '/docs/prime-agent', label: 'Prime Agent' },
      { href: '/docs/import-chats', label: 'Import your chats' },
      { href: '/switch', label: 'Switch to Gab AI' },
    ],
  },
];

export const DOCS_NAV_PATHS = new Set(
  DOCS_NAV_COLUMNS.flatMap((col) => col.items.map((item) => item.href)),
);

const DOCS_NAV_PREFIXES = ['/docs/', '/learn/'];

/** Top-level header items that also appear in the Docs menu. */
const DOCS_EXCLUDED_HREFS = new Set([
  '/api',
]);

export function isDocsNavPath(pathname) {
  if (!pathname) return false;
  if (DOCS_EXCLUDED_HREFS.has(pathname)) return false;
  if (pathname === DOCS_NAV_HUB || pathname === '/learn') return true;
  if (DOCS_NAV_PATHS.has(pathname)) return true;
  return DOCS_NAV_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export const FEATURES_NAV_PATHS = new Set(
  FEATURES_NAV_COLUMNS.flatMap((col) => col.items.map((item) => item.href)),
);

const FEATURES_NAV_PREFIXES = ['/tasks/', '/decks/'];

export function isFeaturesNavPath(pathname) {
  if (!pathname) return false;
  if (pathname === FEATURES_NAV_HUB) return true;
  if (FEATURES_EXCLUDED_HREFS.has(pathname)) return false;
  if (FEATURES_NAV_PATHS.has(pathname)) return true;
  return FEATURES_NAV_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * One sentence per route for the `/features` index, which reads as a directory
 * rather than a list of link text. Kept beside the routes themselves so a new
 * nav entry and its description are one edit; `featuresHubIndex.test.js` fails
 * if a route reaches the hub without one.
 */
export const FEATURE_DESCRIPTIONS = {
  '/images': 'Generate, edit, and inpaint with the leading image models.',
  '/videos': 'Text-to-video and image-to-video, from a sentence or a still.',
  '/music': 'Instrumental and vocal tracks written to a description.',
  '/voice': 'Real-time spoken conversations you can interrupt mid-sentence.',
  '/models': 'GPT, Claude, Gemini, and Arya in a single picker.',
  '/tools': 'Purpose-built generators and assistants that go past chat.',
  '/agents': 'Specialized assistants tuned to the way you actually work.',
  '/tasks': 'Scheduled jobs that keep running while you are away.',
  '/tasks/browse': 'Ready-made task templates to start from.',
  '/decks/recipes': 'Run one prompt across several models and compare answers.',
  '/prompts': 'A library of prompts to copy, edit, and run.',
  '/extension': 'Bring Gab AI alongside your tabs in Chrome and Brave.',
  '/explore': 'Browse what other people are making, and start from it.',
  '/topics': 'Deep dives on the people, places, and ideas in the news.',
  '/blog': 'Product releases, model notes, and how-to writing.',
  '/changelog': 'A running log of new features and improvements, updated as they ship.',
  '/llms': 'A directory of language models and what each one is good at.',
  '/right-wing-ai': 'Arya answers political and religious questions directly.',
  '/based-benchmark': 'Explore our open political alignment rubric and question bank.',
  '/chatgpt-alternative': 'How Gab AI differs, side by side, and how to move over.',
  '/alternatives': 'Every comparison in one place, whichever app you are leaving.',
  '/free-ai-chat': 'What a free account covers, and what credits actually buy.',
  '/switch': 'Import your history from ChatGPT, Claude, or Grok.',
  '/api': 'OpenAI-compatible endpoints on the account you already have.',
  '/docs/api': 'Authentication, endpoints, and request examples.',
  '/docs': 'Guides for every part of the workspace.',
  '/learn': 'A free course on how to use AI — prompts, images, video, music, bots, and agentic workflows.',
  '/use-cases/free-thinkers': 'Straight answers, without the lecture.',
  '/use-cases/small-business': 'Marketing, admin, and support for a small team.',
  '/use-cases/veterans': 'Benefits paperwork, claims, and transition help.',
  '/use-cases/churches': 'Announcements, newsletters, events, volunteers, and outreach.',
  '/use-cases/homeschoolers': 'Curriculum, daily lessons, mixed-age tutoring, and records.',
  '/use-cases/writers': 'Drafting, editing, and research that keeps your voice.',
  '/use-cases/developers': 'Code, review, and an API that drops into your editor.',
  '/use-cases/marketers': 'Campaign copy, creative, and competitive research.',
  '/use-cases/independent-media': 'Research, scripts, and art for a one-person newsroom.',
};

/**
 * Every surface the `/features` hub indexes, grouped for display.
 *
 * Derived from the mega-menu columns so a new nav entry reaches the hub without
 * a second edit, with the developer and persona routes the Features menu
 * deliberately leaves out appended after them.
 */
export function getFeaturesHubSections() {
  const seen = new Set();
  const dedupe = (items) => items
    .filter((item) => {
      if (seen.has(item.href)) return false;
      seen.add(item.href);
      return true;
    })
    .map((item) => ({
      href: item.href,
      label: item.label,
      description: FEATURE_DESCRIPTIONS[item.href],
    }));

  const sections = FEATURES_NAV_COLUMNS.map((col) => ({
    title: col.label,
    links: dedupe(col.items),
  }));

  sections.push({
    title: 'Developers',
    links: dedupe([
      { href: '/api', label: 'API Router' },
      { href: '/docs/api', label: 'API overview' },
      { href: '/docs', label: 'Documentation' },
      { href: '/learn', label: 'AI University' },
    ]),
  });

  sections.push({
    title: 'Use cases',
    links: dedupe(PRIORITY_USE_CASE_LINKS),
  });

  return sections;
}
